"use client";

import { useEffect, useRef } from "react";

type ParticleCanvas = {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
};

type ParticleLoopState = {
  animationFrameId: number;
  frame: number;
  lastFrameTime: number;
  particles: Particle[];
  running: boolean;
};

const FRAME_INTERVAL_MS = 1000 / 30;
const MAX_DISTANCE = 90;
const MAX_DISTANCE_SQ = MAX_DISTANCE * MAX_DISTANCE;
const MAX_PARTICLES = 60;

class Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;

  constructor(private readonly canvasKit: ParticleCanvas) {
    this.x = Math.random() * canvasKit.canvas.width;
    this.y = Math.random() * canvasKit.canvas.height;
    this.size = Math.random() * 2 + 0.5;
    this.speedX = Math.random() * 0.5 - 0.25;
    this.speedY = Math.random() * 0.5 - 0.25;
    this.opacity = Math.random() * 0.5 + 0.1;
  }

  update(): void {
    this.x = wrapPosition(this.x + this.speedX, this.canvasKit.canvas.width);
    this.y = wrapPosition(this.y + this.speedY, this.canvasKit.canvas.height);
  }

  draw(): void {
    const { ctx } = this.canvasKit;
    ctx.fillStyle = `rgba(0, 209, 255, ${this.opacity})`;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.fill();
  }
}

function wrapPosition(value: number, max: number): number {
  if (value > max) return 0;
  if (value < 0) return max;
  return value;
}

function shouldSkipParticles(): boolean {
  if (typeof window === "undefined") return true;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches)
    return true;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

function particleCount(): number {
  return Math.min(Math.floor(window.innerWidth / 25), MAX_PARTICLES);
}

function createParticles(canvasKit: ParticleCanvas): Particle[] {
  return Array.from({ length: particleCount() }, () => new Particle(canvasKit));
}

function connectParticles(
  particles: readonly Particle[],
  ctx: CanvasRenderingContext2D,
): void {
  for (let i = 0; i < particles.length; i += 1) {
    connectParticleAt(i, particles, ctx);
  }
}

function connectParticleAt(
  index: number,
  particles: readonly Particle[],
  ctx: CanvasRenderingContext2D,
): void {
  const source = particles[index];
  if (!source) return;
  for (let j = index + 1; j < particles.length; j += 1) {
    drawParticleConnection(source, particles[j], ctx);
  }
}

function drawParticleConnection(
  source: Particle,
  target: Particle | undefined,
  ctx: CanvasRenderingContext2D,
): void {
  if (!target) return;
  const distanceSq = distanceSquared(source, target);
  if (distanceSq >= MAX_DISTANCE_SQ) return;
  drawConnectionLine(source, target, Math.sqrt(distanceSq), ctx);
}

function distanceSquared(source: Particle, target: Particle): number {
  const dx = source.x - target.x;
  const dy = source.y - target.y;
  return dx * dx + dy * dy;
}

function drawConnectionLine(
  source: Particle,
  target: Particle,
  distance: number,
  ctx: CanvasRenderingContext2D,
): void {
  ctx.strokeStyle = `rgba(0, 209, 255, ${0.1 * (1 - distance / MAX_DISTANCE)})`;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(source.x, source.y);
  ctx.lineTo(target.x, target.y);
  ctx.stroke();
}

function createLoopState(canvasKit: ParticleCanvas): ParticleLoopState {
  return {
    animationFrameId: 0,
    frame: 0,
    lastFrameTime: 0,
    particles: createParticles(canvasKit),
    running: true,
  };
}

function resizeCanvas(
  canvasKit: ParticleCanvas,
  state: ParticleLoopState,
): void {
  canvasKit.canvas.width = window.innerWidth;
  canvasKit.canvas.height = window.innerHeight;
  state.particles = createParticles(canvasKit);
}

function renderParticleFrame(
  canvasKit: ParticleCanvas,
  state: ParticleLoopState,
): void {
  canvasKit.ctx.clearRect(
    0,
    0,
    canvasKit.canvas.width,
    canvasKit.canvas.height,
  );
  state.particles.forEach((particle) => {
    particle.update();
    particle.draw();
  });
  if (state.frame % 2 === 0) connectParticles(state.particles, canvasKit.ctx);
  state.frame += 1;
}

function createAnimationLoop(
  canvasKit: ParticleCanvas,
  state: ParticleLoopState,
): FrameRequestCallback {
  return function animate(time: number): void {
    if (!state.running || document.visibilityState === "hidden") {
      state.running = false;
      return;
    }
    const delta = time - state.lastFrameTime;
    if (delta >= FRAME_INTERVAL_MS) {
      state.lastFrameTime = time - (delta % FRAME_INTERVAL_MS);
      renderParticleFrame(canvasKit, state);
    }
    state.animationFrameId = requestAnimationFrame(animate);
  };
}

function startParticleLoop(canvasKit: ParticleCanvas): () => void {
  const state = createLoopState(canvasKit);
  const animate = createAnimationLoop(canvasKit, state);
  const resize = (): void => resizeCanvas(canvasKit, state);
  const handleVisibility = (): void =>
    toggleParticleLoopVisibility(state, animate);
  window.addEventListener("resize", resize);
  document.addEventListener("visibilitychange", handleVisibility);
  resize();
  state.animationFrameId = requestAnimationFrame(animate);
  return () => stopParticleLoop(state, resize, handleVisibility);
}

function toggleParticleLoopVisibility(
  state: ParticleLoopState,
  animate: FrameRequestCallback,
): void {
  state.running = document.visibilityState !== "hidden";
  cancelAnimationFrame(state.animationFrameId);
  if (state.running) state.animationFrameId = requestAnimationFrame(animate);
}

function stopParticleLoop(
  state: ParticleLoopState,
  resize: () => void,
  handleVisibility: () => void,
): void {
  state.running = false;
  cancelAnimationFrame(state.animationFrameId);
  window.removeEventListener("resize", resize);
  document.removeEventListener("visibilitychange", handleVisibility);
}

/**
 * Animated cyan particle field used behind legacy dashboard surfaces.
 *
 * Example:
 * <ParticlesBackground />
 */
export function ParticlesBackground(
  props: { readonly testId?: string } = {},
): React.ReactNode {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (shouldSkipParticles()) return undefined;
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d", { alpha: true });
    if (!canvas || !ctx) return undefined;
    return startParticleLoop({ canvas, ctx });
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      data-testid={props.testId}
      className="fixed inset-0 z-0 pointer-events-none"
      style={{ background: "transparent" }}
    />
  );
}
